import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Select, Input, Row, Col, Form, DatePicker, Empty, Tooltip, Tabs, Modal, Segmented, Upload, Switch, Drawer, Descriptions } from 'antd';
import {
  PlusOutlined, CheckOutlined, InfoCircleOutlined, CheckCircleOutlined,
  ClockCircleOutlined, InboxOutlined, SendOutlined, MailOutlined, PaperClipOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import TableExportButton from '../../components/TableExportButton';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate } from '../../variables/constants';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';
import { chargerPartenaires, optionsPartenaires } from '../../services/echangesPartenaires';
import SelectNatureCourrier from '../../components/SelectNatureCourrier';
import { largeurTotale, donneesAffichees } from './affichageCourriers';

const { Option } = Select;

const NATURES = avecLibelleTraduit([
  { value: 'BAP' },
  { value: 'CONFIRMATION_GARANTIE' },
  { value: 'TRANSMISSION_EXPERTISE' },
  { value: 'OFFRE_INDEMNISATION' },
  { value: 'RELANCE' },
  { value: 'DEMANDE_PIECES' },
  { value: 'DEMANDE_OFFRE' },
  { value: 'ACCORD_VICTIME' },
  { value: 'TRANSMISSION_PV' },
  { value: 'COMPTE_RENDU_AUDIENCE' },
  { value: 'FICHE_DECLARATION' },
  { value: 'RECOURS' },
  { value: 'AUTRE' },
], 'courriers:natures');
const CANAUX = ['MAIL', 'COURRIER_POSTAL', 'FAX', 'MAIN_PROPRE'];

export default function Courriers() {
  const { t } = useTranslation('courriers');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [typeCourrier, setTypeCourrier] = useState(null);
  /* Le courrier ouvert dans le tiroir de detail, ou null. */
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('non_traites');
  // « Registre » par defaut : voir le commentaire de `parOrigine` plus bas.
  const [origine, setOrigine] = useState('registre');

  // Pièce jointe du courrier : le fichier lui-même est tenu hors du formulaire
  // (un File ne se sérialise pas), le reste des métadonnées passe par le Form.
  const [piece, setPiece] = useState(null);
  const [saving, setSaving] = useState(false);
  // Compagnies et bureaux homologues : un courrier avec un partenaire doit le désigner,
  // pour figurer dans ses états (envoyé à / reçu de / en attente de).
  const [partenaires, setPartenaires] = useState({ bureaux: [], compagnies: [] });
  const reponseAttendue = Form.useWatch('reponseAttendue', form);
  useEffect(() => { chargerPartenaires().then(setPartenaires); }, []);
  const organismePartenaire = (id) => [...partenaires.bureaux, ...partenaires.compagnies].find((o) => o.organismeTrackingId === id);
  const [rechercheSinistre, setRechercheSinistre] = useState('');
  const [sinistresOptions, setSinistresOptions] = useState([]);
  const [sinistreChoisi, setSinistreChoisi] = useState(null);

  /* Recherche de dossier : la pièce ne peut être classée que dans la GED d'un
     dossier - l'arborescence GED est construite par sinistre. */
  useEffect(() => {
    if (!modalOpen || rechercheSinistre.length < 2) { setSinistresOptions([]); return; }
    const t = setTimeout(() => {
      axiosInstance
        .get(`/v1/sinistres?search=${encodeURIComponent(rechercheSinistre)}&size=10`)
        .then(r => {
          const d = r.data?.data;
          setSinistresOptions(Array.isArray(d) ? d : (d?.content || []));
        })
        .catch(() => setSinistresOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [rechercheSinistre, modalOpen]);

  const load = async () => {
    setLoading(true);
    try {
      // Un seul appel : `non-traites` servait a alimenter un etat que plus rien ne
      // lit depuis que les compteurs se calculent sur l'origine selectionnee.
      const allRes = await axiosInstance.get('/v1/courriers').catch(() => ({ data: { data: [] } }));
      setItems(allRes.data?.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const enregistrer = async (vals) => {
    const isEntrant = vals.typeCourrier === 'ENTRANT';
    /* Le champ porte `{ nature, natureLibelle }` : un seul ne distinguerait pas
       « AUTRE, sans plus » de « AUTRE, appelée Mise en demeure ». */
    const nature = vals.nature?.nature || 'AUTRE';
    const natureLibelle = vals.nature?.natureLibelle || null;
    const objet = vals.objet?.trim()
      // Le libellé de la nature libre fait un objet plus parlant que « Sans
      // objet » : c'est bien ce que l'agent vient de nommer.
      || natureLibelle
      // Objet enregistré en base et repris sur les bordereaux : toujours en
      // français, quelle que soit la langue de l'interface.
      || (NATURES.some(n => n.value === nature) ? i18n.t(`courriers:natures.${nature}`, { lng: 'fr' }) : undefined)
      || 'Sans objet';
    const partenaire = organismePartenaire(isEntrant ? vals.expediteurOrganismeTrackingId : vals.destinataireOrganismeTrackingId);
    const expediteur = isEntrant ? (vals.expediteur?.trim() || partenaire?.raisonSociale || 'Non précisé') : 'BNCB-TG';
    const destinataire = isEntrant ? 'BNCB-TG' : (vals.destinataire?.trim() || partenaire?.raisonSociale || 'Non précisé');

    const metadata = {
      typeCourrier: vals.typeCourrier,
      nature,
      natureLibelle,
      referenceCourrier: vals.referenceCourrier?.trim() || null,
      expediteur,
      destinataire,
      objet,
      dateCourrier: vals.dateCourrier?.format('YYYY-MM-DD'),
      dateReception: isEntrant ? vals.dateReception?.format('YYYY-MM-DD') : null,
      canal: vals.canal || 'MAIL',
      sinistreTrackingId: vals.sinistreTrackingId || null,
      titreGed: vals.titreGed?.trim() || null,
      expediteurOrganismeTrackingId: isEntrant ? (vals.expediteurOrganismeTrackingId || null) : null,
      destinataireOrganismeTrackingId: isEntrant ? null : (vals.destinataireOrganismeTrackingId || null),
      reponseAttendue: !isEntrant && !!vals.reponseAttendue,
      echeanceReponse: !isEntrant && vals.reponseAttendue && vals.echeanceReponse ? vals.echeanceReponse.format('YYYY-MM-DD') : null,
    };

    setSaving(true);
    try {
      let res;
      if (piece) {
        // Multipart uniquement quand il y a une pièce : les enregistrements sans
        // fichier continuent de passer par le point d'entrée JSON, inchangé.
        const fd = new FormData();
        fd.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        fd.append('file', piece);
        res = await axiosInstance.post('/v1/courriers', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axiosInstance.post('/v1/courriers', metadata);
      }
      // Le serveur dit si la pièce a bien été classée : la GED peut être
      // indisponible ou refuser le format sans que l'enregistrement échoue.
      const message = res.data?.message || t('liste.enregistre');
      if (/non classée|indisponible/i.test(message)) toast.warning(message);
      else toast.success(message);
      form.resetFields(); setTypeCourrier(null); setPiece(null); setModalOpen(false); load();
    } catch (_) {
    } finally { setSaving(false); }
  };

  const telechargerPiece = async (courrier) => {
    try {
      const res = await axiosInstance.get(
        `/v1/ged/documents/${courrier.ossanGedDocumentId}/telecharger`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courrier.referenceCourrier || 'courrier'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error(t('liste.telechargementImpossible')); }
  };

  const marquerTraite = (courrier) => {
    Modal.confirm({
      title: t('liste.traiter.titre'),
      icon: <CheckCircleOutlined style={{ color: '#1a6b3c' }} />,
      content: (
        <div>
          <p>
            <Trans t={t} i18nKey="liste.traiter.confirmation" values={{ reference: courrier.referenceCourrier || '-' }}
              components={{ strong: <strong /> }} />
          </p>
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
            {t('liste.traiter.aide')}
          </p>
        </div>
      ),
      okText: t('liste.traiter.titre'),
      cancelText: t('liste.traiter.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        try {
          await axiosInstance.patch(`/v1/courriers/${courrier.courrierTrackingId}/traiter`);
          toast.success(t('liste.traiter.succes'));
          load();
        } catch (e) {
          toast.error(e?.response?.data?.message || t('liste.traiter.erreur'));
        }
      },
    });
  };

  const cols = [
    {
      title: t('liste.colonnes.reference'), dataIndex: 'referenceCourrier', key: 'ref', width: 180,
      render: (v, r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {/* L'OBJET SE LIT ICI, AU SURVOL. Il occupait une colonne de 260 px,
              la plus large du tableau, et poussait « Sinistre » hors du champ
              visible. Porte par la reference - l'identite de la ligne - plutot
              que par la ligne entiere : une infobulle large qui suit la souris
              sur vingt lignes gene plus qu'elle n'aide. */}
          <Tooltip title={r.objet || t('liste.sansObjet')} placement="topLeft">
            <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c' }}>{v}</span>
          </Tooltip>
          {r.ossanGedDocumentId && (
            <Tooltip title={t('liste.telechargerPiece')}>
              <PaperClipOutlined
                style={{ color: '#1a6b3c', cursor: 'pointer' }}
                onClick={() => telechargerPiece(r)}
              />
            </Tooltip>
          )}
          {/* Pièce déposée mais OCR encore en cours : l'identifiant numérique
              n'arrive qu'à la fin de l'indexation. */}
          {!r.ossanGedDocumentId && r.ossanGedDocumentTrackingId && (
            <Tooltip title={t('liste.indexationEnCours')}>
              <PaperClipOutlined style={{ color: '#9ca3af' }} />
            </Tooltip>
          )}
        </span>
      ),
      export: (v) => v || ''
    },
    {
      title: t('liste.colonnes.type'), dataIndex: 'typeCourrier', key: 'type', width: 90,
      render: (v) => <Tag color={v === 'ENTRANT' ? 'blue' : 'green'} style={{ borderRadius: 20 }}>{v}</Tag>,
      export: (v) => v || ''
    },
    {
      /* Le libellé propre l'emporte : un courrier enregistré comme AUTRE mais
         nommé « Mise en demeure » doit se lire ainsi, sinon la liste affiche
         « Autre » sur tout ce que le bureau a pris la peine de nommer. */
      title: t('liste.colonnes.nature'), dataIndex: 'nature', key: 'nat', width: 180,
      render: (v, r) => {
        const libelle = r?.natureLibelle
          || NATURES.find(x => x.value === v)?.label
          || v;
        return <Tag style={{ borderRadius: 20 }}>{libelle}</Tag>;
      },
      export: (v) => { const n = NATURES.find(x => x.value === v); return n?.label || v || ''; }
    },
    /* LA COLONNE « OBJET » A ETE RETIREE, pas oubliee. Elle pesait 260 px, la plus
       large du tableau, et poussait « Sinistre » au pixel 1260 : hors du champ
       visible sur un portable, sans que rien ne le signale.

       L'objet n'est pas perdu pour autant : il se lit au survol de la reference,
       et en entier dans le tiroir de detail. Il reste exporte - un fichier n'a pas
       de contrainte de largeur.

       L'HISTOIRE DE CETTE LIGNE mérite d'etre connue. Le meme defaut s'est produit
       une premiere fois en sens inverse : la somme des largeurs valait deja 1300
       pour un `scroll.x` de 1300, et c'est « Objet » qui s'affichait sur zero
       pixel. Le correctif avait ajuste `scroll.x` a la main. Le nombre a re-derive
       des la colonne suivante. `scroll.x` est desormais CALCULE - voir
       `largeurTotale` - pour qu'il n'y ait plus de nombre a tenir a jour. */
    /* `hidden` et non une suppression : la colonne reste dans `cols`, donc
       `exportUtils` continue de l'exporter - il filtre sur `exportable`, pas sur
       `hidden`. Un fichier Excel n'a pas de contrainte de largeur, et l'objet y
       est la colonne la plus utile. */
    { title: t('liste.colonnes.objet'), dataIndex: 'objet', key: 'obj', hidden: true },
    { title: t('liste.colonnes.expediteur'), dataIndex: 'expediteur', key: 'exp', width: 160, ellipsis: true },
    { title: t('liste.colonnes.destinataire'), dataIndex: 'destinataire', key: 'dest', width: 160, ellipsis: true },
    {
      title: t('liste.colonnes.canal'), dataIndex: 'canal', key: 'can', width: 110,
      render: (v) => <Tag style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>,
      export: (v) => v || ''
    },
    { title: t('liste.colonnes.dateCourrier'), dataIndex: 'dateCourrier', key: 'dc', width: 120, format: 'date', render: (v) => formatDate(v) },
    {
      title: t('liste.colonnes.sinistre'), dataIndex: 'sinistreNumeroLocal', key: 'sin', width: 140,
      render: (v) => v ? <span style={{ fontWeight: 600, color: '#1a6b3c' }}>{v}</span> : '-',
      export: (v) => v || ''
    },
    {
      title: t('liste.colonnes.traite'), dataIndex: 'traite', key: 'tr', width: 90, align: 'center',
      format: 'bool',
      render: (v) => v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('liste.oui')}</Tag>
        : <Tag color="orange" style={{ borderRadius: 20 }}>{t('liste.non')}</Tag>
    },
    {
      title: '', key: 'act', width: 90, exportable: false,
      render: (_, r) => !r.traite && (
        <Button size="small" icon={<CheckOutlined />} type="primary"
          style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 6 }}
          /* La ligne entiere ouvre le tiroir : sans cet arret, traiter un
             courrier ouvrirait aussi son detail, et la confirmation s'afficherait
             par-dessus un tiroir qu'on n'a pas demande. */
          onClick={(e) => { e.stopPropagation(); marquerTraite(r); }}>{t('liste.traiterBouton')}</Button>
      )
    },
  ];

  /**
   * Deux populations dans la meme table, qu'il faut cesser de melanger.
   *
   * Un courrier SAISI passe par CourrierServiceImpl, qui lui rattache toujours un
   * registre journalier - explicitement, ou en le creant. La synchronisation IMAP,
   * elle, enregistre CHAQUE message de la boite de reception en `Courrier` sans
   * jamais toucher au registre. `registreJourTrackingId` distingue donc de facon
   * fiable le courrier officiel du mail rapatrie.
   *
   * Sans cette separation, le registre du BNCB affichait les newsletters et les
   * notifications SharePoint au meme rang qu'un courrier d'homologue - et chacune
   * consommait une reference officielle (LKS/2026/000xx). D'ou les 87 « non
   * traites » que personne ne traitera jamais.
   *
   * Le tri se fait ici, a l'affichage : aucun mail n'est supprime, et un message
   * qui merite d'entrer au registre reste consultable dans l'autre onglet.
   */
  const parOrigine = origine === 'tous'
    ? items
    : origine === 'registre'
      ? items.filter((c) => c.registreJourTrackingId)
      : items.filter((c) => !c.registreJourTrackingId);

  /* LE SEGMENT S'APPLIQUE AUX TROIS ONGLETS. Il ne portait que sur « non
     traites » : « traites » et « tous » relisaient `items` et laissaient entrer
     les mails synchronises, dont la colonne Sinistre est vide par construction,
     alors que les libelles d'onglets comptaient `parOrigine`. L'onglet annoncait
     un nombre, la table en montrait un autre. Voir `affichageCourriers`. */
  const dataAffichee = donneesAffichees(items, origine, activeTab);

  return (
    <Layout title={t('titre')}>
      {/* `dataAffichee` et non `items` : l'ecran est segmente (registre / mails /
          traites), le compte doit suivre la selection courante, comme le tableau. */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<MailOutlined />}
        compte={dataAffichee.length}
        unite={t('liste.unite')}
        actions={(
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
            {t('liste.enregistrerCourrier')}
          </Button>
        )}
      />

      <Segmented
        value={origine}
        onChange={setOrigine}
        style={{ marginBottom: 16 }}
        options={[
          { label: t('liste.origines.registre', { n: items.filter((c) => c.registreJourTrackingId).length }), value: 'registre' },
          { label: t('liste.origines.mails', { n: items.filter((c) => !c.registreJourTrackingId).length }), value: 'mails' },
          { label: t('liste.origines.tous', { n: items.length }), value: 'tous' },
        ]}
      />

      <KpiCards
        items={[
          { label: t('liste.kpis.nonTraites'), value: parOrigine.filter(c => !c.traite).length, icon: <ClockCircleOutlined />, color: '#991b1b', bg: '#fee2e2' },
          { label: t('liste.kpis.entrants'), value: parOrigine.filter(c => c.typeCourrier === 'ENTRANT').length, icon: <InboxOutlined />, color: '#1e40af', bg: '#dbeafe' },
          { label: t('liste.kpis.sortants'), value: parOrigine.filter(c => c.typeCourrier === 'SORTANT').length, icon: <SendOutlined />, color: '#166534', bg: '#dcfce7' },
          { label: t('liste.kpis.total'), value: parOrigine.length, icon: <MailOutlined />, color: '#374151', bg: '#f4f6f5' },
        ]}
      />

      <div className="cb-card" style={{ padding: 0 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} className="cb-tabs-green"
          items={[
            { key: 'non_traites', label: t('liste.onglets.nonTraites', { n: parOrigine.filter(c => !c.traite).length }) },
            { key: 'traites', label: t('liste.onglets.traites', { n: parOrigine.filter(c => c.traite).length }) },
            { key: 'tous', label: t('liste.onglets.tous', { n: parOrigine.length }) },
          ]} />
        <Table className="cb-table"
          dataSource={dataAffichee}
          columns={cols} rowKey="courrierTrackingId"
          loading={loading}
          /* CALCULE, jamais ecrit a la main : un nombre en dur se desynchronise de
             la somme des colonnes des la premiere colonne ajoutee, et la derniere
             declaree sort du champ visible sans que rien ne le signale. C'est
             arrive deux fois sur ce tableau. */
          scroll={{ x: largeurTotale(cols) }}
          onRow={(r) => ({
            onClick: () => setDetail(r),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 20, showTotal: (n) => t('liste.total', { count: n }) }}
          locale={{ emptyText: <Empty description={t('liste.aucun')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <TableExportButton
        floating
        data={dataAffichee}
        columns={cols}
        filename={`courriers-${activeTab}`}
        title={t('titre')}
        subtitle={
          activeTab === 'non_traites' ? t('liste.export.nonTraites')
            : activeTab === 'traites' ? t('liste.export.traites')
              : t('liste.export.tous')
        }
        count={dataAffichee.length}
      />

      <CBModal title={t('liste.enregistrerCourrier')} open={modalOpen}
        onCancel={() => { form.resetFields(); setTypeCourrier(null); setModalOpen(false); }}
        footer={null} width={640} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer}
          onValuesChange={(changed) => { if (changed.typeCourrier !== undefined) setTypeCourrier(changed.typeCourrier); }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="typeCourrier" label={t('form.type')}
                rules={[{ required: true, message: t('form.typeObligatoire') }]}>
                <Select placeholder={t('form.typePlaceholder')} allowClear>
                  <Option value="ENTRANT"><Tag color="blue" style={{ borderRadius: 20 }}>ENTRANT</Tag></Option>
                  <Option value="SORTANT"><Tag color="green" style={{ borderRadius: 20 }}>SORTANT</Tag></Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nature" label={t('form.nature')}>
                <SelectNatureCourrier naturesOfficielles={NATURES} style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            {typeCourrier === 'ENTRANT' && (
              <>
                <Col span={12}>
                  <Form.Item name="expediteurOrganismeTrackingId" label={t('form.partenaireExpediteur')}
                    tooltip={t('form.partenaireExpediteurAide')}>
                    <Select allowClear showSearch optionFilterProp="label" placeholder={t('form.partenairePlaceholder')}
                      options={optionsPartenaires(partenaires)} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="expediteur" label={t('form.autreExpediteur')}>
                    <Input placeholder={t('form.autreExpediteurPlaceholder')} />
                  </Form.Item>
                </Col>
              </>
            )}
            {typeCourrier === 'SORTANT' && (
              <>
                <Col span={12}>
                  <Form.Item name="destinataireOrganismeTrackingId" label={t('form.partenaireDestinataire')}
                    tooltip={t('form.partenaireDestinataireAide')}>
                    <Select allowClear showSearch optionFilterProp="label" placeholder={t('form.partenairePlaceholder')}
                      options={optionsPartenaires(partenaires)} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="destinataire" label={t('form.autreDestinataire')}>
                    <Input placeholder={t('form.autreDestinatairePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="reponseAttendue" label={t('form.reponseAttendue')} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                {reponseAttendue && (
                  <Col span={12}>
                    <Form.Item name="echeanceReponse" label={t('form.echeanceReponse')}>
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}

            <Col span={24}>
              <Form.Item name="objet"
                label={
                  <span>
                    {t('form.objet')}&nbsp;
                    <Tooltip title={t('form.objetAide')}>
                      <InfoCircleOutlined style={{ color: '#9ca3af' }} />
                    </Tooltip>
                  </span>
                }>
                <Input placeholder={t('form.objetPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="referenceCourrier"
                label={
                  <span>
                    {t('form.reference')}&nbsp;
                    <Tooltip title={t('form.referenceAide')}>
                      <InfoCircleOutlined style={{ color: '#9ca3af' }} />
                    </Tooltip>
                  </span>
                }>
                <Input placeholder={t('form.referencePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="canal" label={t('form.canal')}>
                <Select placeholder={t('form.canal')} allowClear>
                  {CANAUX.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateCourrier" label={t('form.dateCourrier')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            {typeCourrier === 'ENTRANT' && (
              <Col span={12}>
                <Form.Item name="dateReception" label={t('form.dateReception')}>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Form.Item name="sinistreTrackingId" label={t('form.dossierLie')}>
                <Select
                  showSearch allowClear
                  placeholder={t('form.dossierPlaceholder')}
                  filterOption={false}
                  onSearch={setRechercheSinistre}
                  onChange={(v) => setSinistreChoisi(
                    sinistresOptions.find(o => o.sinistreTrackingId === v) || null)}
                  notFoundContent={rechercheSinistre.length < 2 ? t('form.deuxCaracteres') : t('form.aucunResultat')}
                  options={sinistresOptions.map(s => ({
                    value: s.sinistreTrackingId,
                    label: `${s.numeroSinistreLocal} · ${s.assureNomComplet || '-'}`,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={t('form.piece')}
                extra={sinistreChoisi
                  ? t('form.pieceAide')
                  : t('form.pieceSansDossier')}
              >
                <Upload
                  beforeUpload={(file) => { setPiece(file); return false; }}
                  onRemove={() => setPiece(null)}
                  fileList={piece ? [{ uid: '1', name: piece.name }] : []}
                  maxCount={1}
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={!sinistreChoisi}
                >
                  <Button icon={<PaperClipOutlined />} disabled={!sinistreChoisi}>
                    {t('form.joindre')}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            {piece && (
              <Col span={24}>
                <Form.Item
                  name="titreGed"
                  label={t('form.titreGed')}
                  extra={t('form.titreGedAide')}
                >
                  <Input placeholder={t('form.titreGedPlaceholder')} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => {
              form.resetFields(); setTypeCourrier(null); setPiece(null); setSinistreChoisi(null); setModalOpen(false);
            }}>{t('form.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={saving}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {t('form.enregistrer')}
            </Button>
          </div>
        </Form>
      </CBModal>

      {/* ── Tiroir de detail ──────────────────────────────────────────────────
          A GAUCHE, comme demande. Il porte ce que la table ne montre plus - l'objet
          en entier d'abord - et ce qu'elle n'a jamais montre faute de place : le
          bordereau, le registre, l'echeance de reponse, le statut d'envoi.

          Le tiroir LIT, il ne modifie pas. Mettre un formulaire ici dupliquerait la
          fenetre d'enregistrement, et deux chemins d'ecriture divergent toujours. */}
      <Drawer
        placement="right"
        width={520}
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? detail.referenceCourrier : ''}
        destroyOnHidden
      >
        {detail && (
          <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 180 }}>
            {/* L'objet EN PREMIER : c'est lui qu'on vient chercher, puisqu'il a
                quitte le tableau. */}
            <Descriptions.Item label={t('liste.detail.objet')}>{detail.objet || t('liste.sansObjet')}</Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.type')}>
              <Tag color={detail.typeCourrier === 'ENTRANT' ? 'blue' : 'green'} style={{ borderRadius: 20 }}>
                {detail.typeCourrier}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.nature')}>
              {detail.natureLibelle
                || NATURES.find((x) => x.value === detail.nature)?.label
                || detail.nature || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.expediteur')}>
              {detail.expediteurOrganismeLibelle || detail.expediteur || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.destinataire')}>
              {detail.destinataireOrganismeLibelle || detail.destinataire || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.canal')}>{detail.canal || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.dateCourrier')}>{formatDate(detail.dateCourrier) || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.dateReception')}>{formatDate(detail.dateReception) || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.reponseAttendue')}>
              {detail.reponseAttendue
                ? (detail.dateReponse
                  ? t('liste.detail.reponseRecueLe', { date: formatDate(detail.dateReponse) })
                  : (detail.echeanceReponse
                    ? t('liste.detail.reponseEnAttenteAvant', { date: formatDate(detail.echeanceReponse) })
                    : t('liste.detail.reponseEnAttente')))
                : t('liste.non')}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.dossier')}>
              {detail.sinistreNumeroLocal
                ? <span style={{ fontWeight: 600, color: '#1a6b3c' }}>{detail.sinistreNumeroLocal}</span>
                /* On DIT pourquoi c'est vide. Un tiret laisse croire a une perte de
                   donnee ; un mail rapatrie n'a simplement jamais ete rattache. */
                : (detail.registreJourTrackingId
                  ? t('liste.detail.aucunDossier')
                  : t('liste.detail.mailSansDossier'))}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.bordereau')}>
              {detail.numeroBordereau
                ? (detail.ordreDansBordereau
                  ? t('liste.detail.bordereauLigne', { numero: detail.numeroBordereau, ligne: detail.ordreDansBordereau })
                  : detail.numeroBordereau)
                : t('liste.detail.sansBordereau')}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.registre')}>
              {detail.registreJourTrackingId ? t('liste.detail.inscrit') : t('liste.detail.nonInscrit')}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.serviceDestinataire')}>
              {detail.serviceDestinataireInterne || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.piece')}>
              {detail.ossanGedDocumentId
                ? (
                  <Button size="small" icon={<PaperClipOutlined />}
                    onClick={() => telechargerPiece(detail)}
                    style={{ borderColor: '#1a6b3c', color: '#1a6b3c' }}>
                    {t('liste.detail.telecharger')}
                  </Button>
                )
                : detail.ossanGedDocumentTrackingId
                  ? t('liste.detail.indexationEnCours')
                  : t('liste.detail.aucunePiece')}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.envoiMail')}>
              {detail.envoyeParMail
                ? (detail.dateEnvoi
                  ? t('liste.detail.envoyeLe', { statut: detail.statutEnvoi || t('liste.detail.envoye'), date: formatDate(detail.dateEnvoi) })
                  : (detail.statutEnvoi || t('liste.detail.envoye')))
                : t('liste.non')}
            </Descriptions.Item>
            <Descriptions.Item label={t('liste.detail.traite')}>
              {detail.traite
                ? <Tag color="green" style={{ borderRadius: 20 }}>{detail.dateTraitement ? t('liste.detail.traiteLe', { date: formatDate(detail.dateTraitement) }) : t('liste.oui')}</Tag>
                : <Tag color="orange" style={{ borderRadius: 20 }}>{t('liste.non')}</Tag>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Layout>
  );
}
